<template>
  <div>
    <!-- Sign In Modal -->
    <b-modal
      id="sign-in-modal"
      ref="signInModal"
      title="Sign In"
      @shown="focusInput('signInEmail')"
      @hidden="signIn = {}"
      v-model="signInModalOpen"
      hide-footer>
      <b-form
        id="sign-in-form"
        name="signInForm"
        @submit.prevent="submitSignIn">
        <!-- Email -->
        <div class="row">
          <div class="col-lg">
            <label for="sign-in-email">
              Email
            </label>
            <b-form-input
              id="sign-in-email"
              ref="signInEmail"
              name="signInEmail"
              type="email"
              placeholder="Enter your email address"
              v-model="signIn.username"
              :disabled="!!signInLoadingMsg">
            </b-form-input>
          </div>
        </div>
        <!-- / Email -->

        <!-- Password -->
        <div class="row mt-3">
          <div class="col-lg">
            <label for="sign-in-password">
              Password
            </label>
            <b-form-input
              id="sign-in-password"
              ref="signInPassword"
              name="signInPassword"
              type="password"
              placeholder="Enter your password"
              v-model="signIn.password"
              :disabled="!!signInLoadingMsg">
            </b-form-input>
          </div>
        </div>
        <!-- / Password -->


        <!-- Form Buttons -->
        <div class="row align-items-end justify-content-end mt-4">
          <div class="col">
            <b-button
              class="p-0"
              type="button"
              variant="link"
              :disabled="!!signInLoadingMsg"
              @click="signUpInstead">
              Don't have an account?
            </b-button><br>

            <b-button
              class="p-0"
              type="button"
              variant="link"
              :disabled="!!signInLoadingMsg"
              @click="openForgotPassword">
              Forgot password?
            </b-button>
          </div>

          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <b-button
              type="submit"
              class="w-100 w-lg-auto"
              variant="success"
              :disabled="!!signInLoadingMsg">
              Sign In
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              type="button"
              class="w-100 w-lg-auto"
              variant="danger"
              @click="signInModalOpen = false;"
              :disabled="!!signUpLoadingMsg">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="sign-in-loading"
        variant="light"
        :msg="signInLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- / Sign In Modal -->

    <!-- Forgot Password Modal -->
    <b-modal
      id="forgot-password-modal"
      ref="forgotPasswordModal"
      title="Forgot password?"
      @shown="focusInput('forgotPasswordEmail')"
      @hidden="forgotPassword = {}"
      v-model="forgotPasswordModalOpen"
      hide-footer>
      <b-form
        id="forgot-password-form"
        name="forgotPasswordForm"
        @submit.prevent="submitForgotPassword">
        <!-- Instructions -->
        <div class="row">
          <div class="col-lg">
            <p>
              Enter your email address and your password will be reset
              and sent to you via email. We recommend that you change
              your password once you are able to log in.
            </p>
          </div>
        </div>
        <!-- / Instructions -->

        <!-- Email -->
        <div class="row">
          <div class="col-lg">
            <label for="forgot-password-email">
              Email
            </label>
            <b-form-input
              id="forgot-password-email"
              ref="forgotPasswordEmail"
              name="forgotPasswordEmail"
              type="email"
              placeholder="Enter your email address"
              v-model="forgotPassword.email"
              :disabled="!!forgotPasswordLoadingMsg">
            </b-form-input>
          </div>
        </div>
        <!-- / Email -->

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <div class="col-lg-auto pr-lg-0">
            <b-button
              type="submit"
              class="w-100 w-lg-auto"
              variant="success"
              :disabled="!!forgotPasswordLoadingMsg">
              Submit
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              type="button"
              class="w-100 w-lg-auto"
              variant="danger"
              @click="forgotPasswordModalOpen = false;"
              :disabled="!!forgotPasswordLoadingMsg">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="forgot-password-loading"
        variant="light"
        :msg="forgotPasswordLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- / Forgot Password Modal -->

    <!-- Sign Up Modal -->
    <b-modal
      id="sign-up-modal"
      ref="signUpModal"
      title="Sign up for an account"
      size="lg"
      @shown="focusInput('signUpFirstName')"
      @hidden="signUp = {}; passwordConfirm = '';"
      v-model="signUpModalOpen"
      hide-footer>
      <b-form
        id="sign-up-form"
        name="signUpForm"
        @submit.prevent="submitSignUp">

        <!-- First / Last Name -->
        <div class="row">
          <!-- First Name -->
          <div class="col-lg">
            <label for="sign-up-first-name">
              First Name
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="sign-up-first-name"
              ref="signUpFirstName"
              name="signUpFirstName"
              type="text"
              placeholder="Enter your first name"
              :disabled="!!signUpLoadingMsg"
              v-model="signUp.first_name">
            </b-form-input>
          </div>
          <!-- / First Name -->

          <!-- Last Name -->
          <div class="col-lg mt-3 mt-lg-0">
            <label for="sign-up-last-name">
              Last Name
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="sign-up-last-name"
              ref="signUpLastName"
              name="signUpLastName"
              type="text"
              placeholder="Enter your last name"
              :disabled="!!signUpLoadingMsg"
              v-model="signUp.last_name">
            </b-form-input>
          </div>
          <!-- / Last Name -->
        </div>
        <!-- / First / Last Name -->

        <!-- Email / Password -->
        <div class="row mt-3">
          <!-- Email -->
          <div class="col-lg">
            <label for="sign-up-email">
              Email
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="sign-up-email"
              ref="signUpEmail"
              name="signUpEmail"
              type="email"
              placeholder="Enter your email address"
              v-model="signUp.email"
              :disabled="!!signUpLoadingMsg">
            </b-form-input>
          </div>
          <!-- / Email -->

          <!-- Password -->
          <div class="col-lg mt-3 mt-lg-0">
            <label for="sign-up-password">
              Password
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="sign-up-password"
              ref="signUpPassword"
              name="signUpPassword"
              type="password"
              placeholder="Enter your desired password"
              v-model="signUp.password"
              :disabled="!!signInLoadingMsg">
            </b-form-input>
          </div>
          <!-- / Password -->

          <!-- Password Confirm -->
          <div class="col-lg mt-3 mt-lg-0">
            <label for="sign-up-password-confirm">
              Confirm Password
              <small class="text-danger">required</small>
            </label>
            <b-form-input
              id="sign-up-password-confirm"
              ref="signUpPasswordConfirm"
              name="signUpPasswordConfirm"
              type="password"
              placeholder="Confirm your desired password"
              v-model="passwordConfirm"
              :disabled="!!signInLoadingMsg">
            </b-form-input>
          </div>
          <!-- / Password Confirm -->
        </div>
        <!-- / Email / Password -->

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <div class="col-lg-auto pr-lg-0">
            <b-button
              type="submit"
              class="w-100 w-lg-auto"
              variant="success"
              :disabled="!!signUpLoadingMsg || signUpInvalid">
              Sign Up
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              type="button"
              class="w-100 w-lg-auto"
              variant="danger"
              @click="signUpModalOpen = false;"
              :disabled="!!signUpLoadingMsg">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="sign-up-loading"
        variant="light"
        :msg="signUpLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- / Sign Up Modal -->
  </div>
</template>

<script>
import DataSvc from '../../services/data';
import AppMixin from '../../services/mixin.app';
import Session from '../../services/user-session';

export default {
  name: 'SignIn',
  mixins: [
    AppMixin,
  ],
  data() {
    return {
      // sign in
      signInModalOpen: false,
      signInLoadingMsg: '',
      signIn: {
        username: process.env.VUE_APP_USERNAME,
        password: process.env.VUE_APP_PASSWORD,
      },

      // forgot password
      forgotPasswordModalOpen: false,
      forgotPasswordLoadingMsg: '',
      forgotPassword: {},

      // sign up
      signUpModalOpen: false,
      signUpLoadingMsg: '',
      signUp: {},
      passwordConfirm: '',
    };
  },
  computed: {
    signUpInvalid() {
      return !this.signUp.first_name
        || !this.signUp.last_name
        || !this.signUp.email
        || !this.signUp.password
        || !this.passwordConfirm
        || this.signUp.password !== this.passwordConfirm;
    },
  },
  methods: {
    // sign in
    submitSignIn() {
      this.signInLoadingMsg = 'Signing you in...';
      return DataSvc.signIn(this.signIn)
        .then((user) => {
          this.loggedInUser = user;
          this.signInLoadingMsg = 'Retrieving user data...';

          // get other data
          return Session.begin(user);
        })
        .then((data) => {
          [
            this.registrationSession,
            this.closedRegistrations,
          ] = data;

          this.signInModalOpen = false;
          this.$root.$emit('resolve-sign-in', true);
        })
        .finally(() => {
          this.signInLoadingMsg = '';
        });
    },

    // forgot password
    openForgotPassword() {
      this.forgotPasswordModalOpen = true;
    },
    submitForgotPassword() {
      this.forgotPasswordLoadingMsg = true;
      return DataSvc.forgotPassword(this.forgotPassword)
        .then(() => {
          this.showWarning(
            `Your password has been reset and sent to you via email.
            We recommend that you change your password once you are able to log in.`,
            'Password reset!',
          );
          this.forgotPasswordModalOpen = false;
        })
        .finally(() => {
          this.forgotPasswordLoadingMsg = false;
        });
    },

    // sign up
    signUpInstead() {
      this.signInModalOpen = false;
      this.showSignUp()
        .then(() => {
          this.$root.$emit('resolve-sign-in', true);
        });
    },
    submitSignUp() {
      this.signUpLoadingMsg = 'Signing you up...';
      return DataSvc.signUp(this.signUp)
        .then((user) => {
          Session.begin(user);
          this.loggedInUser = user;
          this.signUpModalOpen = false;
          this.$root.$emit('resolve-sign-up', true);
        })
        .finally(() => {
          this.signUpLoadingMsg = '';
        });
    },
  },
  created() {
    this.$root.$on('show-sign-in', () => {
      this.signInModalOpen = true;
    });

    this.$root.$on('show-sign-up', () => {
      this.signUpModalOpen = true;
    });
  },
};
</script>
